<template>
    <form novalidate @submit.prevent="onSave">
        <PaddedContentTop>
            <div class="space-y-4">
                <div class="bg-gray-50 p-4 space-y-4">
                    <TextField
                        :value="values.firstName"
                        :label="$t('first-name')"
                        :error-messages="errors.firstName"
                        autocomplete="off"
                        :required="true"
                        @input="(event) => setFieldValue('firstName', (event.target as HTMLInputElement).value)"
                    />
                    <TextField
                        :value="values.lastName"
                        :label="$t('last-name')"
                        :error-messages="errors.lastName"
                        autocomplete="off"
                        :required="true"
                        @input="(event) => setFieldValue('lastName', (event.target as HTMLInputElement).value)"
                    />
                    <EmailField
                        :value="values.email"
                        :label="$t('email')"
                        :error-messages="errors.email"
                        autocomplete="off"
                        :required="true"
                        @input="(event) => setFieldValue('email', (event.target as HTMLInputElement).value)"
                    />
                    <PasswordField
                        :value="values.password"
                        :label="$t('password')"
                        :error-messages="errors.password"
                        autocomplete="new-password"
                        :required="true"
                        :min-length="userCreateSchema.shape.password.minLength ?? undefined"
                        :description="
                            $t('password-min-length-description', {
                                count: userCreateSchema.shape.password.minLength ?? '',
                            })
                        "
                        @input="(event) => setFieldValue('password', (event.target as HTMLInputElement).value)"
                    />
                </div>

                <div class="bg-gray-50 h-full p-4">
                    <h2 class="font-semibold leading-none text-gray-700 text-xl mb-3">
                        {{ $t('permissions') }}
                    </h2>
                    <RadioButtonGroup
                        :label="$t('access-permission')"
                        name="access-permission"
                        :is-required="true"
                        :options="accessPermissionOptions"
                        @change="handlePermissionChange"
                    />
                </div>

                <div class="flex justify-end gap-2 px-4">
                    <Button v-if="isTouched" size="Base" variant="Danger-Light" @click="onDiscard">
                        <template #icon>
                            <UndoIcon class="size-5" />
                        </template>
                        {{ $t('discard') }}
                    </Button>
                    <Button v-else size="Base" variant="Neutral-Light" @click="$router.back()">
                        <template #icon>
                            <CloseIcon class="size-5" />
                        </template>
                        {{ $t('cancel') }}
                    </Button>
                    <Button
                        size="Base"
                        variant="Info-Strong"
                        :disabled-explanation="saveButtonDisabledExplanation"
                        :status="createStatus"
                        :status-message="createStatusMessage"
                        type="submit"
                    >
                        <template #icon>
                            <AllDoneIcon class="size-5" />
                        </template>
                        {{ $t('create') }}
                    </Button>
                </div>
            </div>
        </PaddedContentTop>
    </form>
</template>

<script setup lang="ts">
import { ButtonStatus } from '@/components/Button/Button.types';
import Button from '@/components/Button/Button.vue';
import EmailField from '@/components/Form/EmailField.vue';
import PasswordField from '@/components/Form/PasswordField.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import TextField from '@/components/Form/TextField.vue';
import AllDoneIcon from '@/components/Icon/AllDoneIcon.vue';
import CloseIcon from '@/components/Icon/CloseIcon.vue';
import UndoIcon from '@/components/Icon/UndoIcon.vue';
import PaddedContentTop from '@/components/Layout/PaddedContentTop.vue';
import { useForm } from '@/forms/composables/useForm';
import { $t } from '@/plugins/fluent';
import router from '@/plugins/router';
import { computed, ref } from 'vue';
import { useUserCreate } from '../composables/useUserCreate';
import { userCreateSchema } from './schemas';

const { values, errors, setFieldValue, isTouched, onDiscard, saveButtonDisabledExplanation, validate } = useForm(
    userCreateSchema,
    computed(() => ({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        isAdmin: false,
    })),
);

const accessPermissionOptions = computed(() => [
    {
        id: 'read-and-write',
        label: $t('read-and-write'),
        isChecked: values.value.isAdmin,
        isDisabled: false,
        value: 'read-and-write',
    },
    {
        id: 'read',
        label: $t('read'),
        isChecked: !values.value.isAdmin,
        isDisabled: false,
        value: 'read',
    },
    {
        id: 'no-read-or-write',
        label: $t('no-read-or-write'),
        isChecked: false,
        isDisabled: true,
        value: 'no-read-or-write',
        helpButtonText: $t('no-read-or-write-help'),
    },
]);

function handlePermissionChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value === 'read-and-write') {
        setFieldValue('isAdmin', true);
    } else {
        setFieldValue('isAdmin', false);
    }
}

const { create, createUserMutation } = useUserCreate();
const { loading: isCreating } = createUserMutation;
const hasCreateError = ref(false);

const createStatus = computed<ButtonStatus>(() => {
    if (hasCreateError.value) {
        return 'Error';
    }

    if (isCreating.value) {
        return 'Loading';
    }

    return 'Default';
});

const createStatusMessage = computed(() => (hasCreateError.value ? $t('create-dev-account-failed') : undefined));

async function onSave() {
    const isValid = await validate();

    if (!isValid) {
        return;
    }

    hasCreateError.value = false;

    try {
        const userId = await create(values.value);
        router.push({ name: 'user-details', params: { userId } });
    } catch {
        hasCreateError.value = true;
    }
}
</script>
